import React, { Component, ErrorInfo, ReactNode, useState, useEffect } from 'react';
import { Alert, AlertTitle, Button, Box, Typography, CircularProgress } from '@mui/material';
import { WifiOff, ErrorOutline, Security } from '@mui/icons-material';
import { ConnectionState, checkFirestoreConnection, refreshFirebaseAuth, reloadAppWithCleanState, monitorFirestoreConnection } from '../utils/network';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isOffline: boolean;
  connectionState: ConnectionState;
  isCheckingConnection: boolean;
  retryCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  private connectionMonitor: (() => void) | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      isOffline: !navigator.onLine,
      connectionState: ConnectionState.UNKNOWN,
      isCheckingConnection: false,
      retryCount: 0
    };

    // Add event listeners for online/offline status
    window.addEventListener('online', this.handleOnlineStatusChange);
    window.addEventListener('offline', this.handleOnlineStatusChange);
  }

  componentDidMount() {
    this.startConnectionMonitoring();
  }

  componentWillUnmount() {
    // Clean up event listeners
    window.removeEventListener('online', this.handleOnlineStatusChange);
    window.removeEventListener('offline', this.handleOnlineStatusChange);

    // Clean up connection monitor
    if (this.connectionMonitor) {
      this.connectionMonitor();
    }
  }

  startConnectionMonitoring = () => {
    // Clean up existing monitor if any
    if (this.connectionMonitor) {
      this.connectionMonitor();
    }

    // Start new monitor
    this.connectionMonitor = monitorFirestoreConnection(
      // Connected
      () => {
        this.setState({ 
          connectionState: ConnectionState.ONLINE,
          isCheckingConnection: false,
          retryCount: 0
        });
      },
      // Disconnected
      () => {
        this.setState({ 
          connectionState: ConnectionState.OFFLINE,
          isCheckingConnection: false
        });
      },
      // Auth Error
      () => {
        this.setState({ 
          connectionState: ConnectionState.AUTH_ERROR,
          isCheckingConnection: false
        });
      },
      // Other Error
      (error) => {
        this.setState({ 
          connectionState: ConnectionState.OTHER_ERROR,
          error: error || new Error("Unknown Firestore error"),
          isCheckingConnection: false
        });
      },
      // Check interval - more frequent when there are issues
      this.state.hasError || this.state.connectionState !== ConnectionState.ONLINE 
        ? 15000  // 15 seconds when there are issues
        : 60000  // 1 minute when everything is fine
    );
  }

  handleOnlineStatusChange = () => {
    const isOffline = !navigator.onLine;
    this.setState({ isOffline });
    
    // If we're back online, check Firestore connection
    if (!isOffline) {
      this.checkConnection();
    } else {
      this.setState({ connectionState: ConnectionState.OFFLINE });
    }
  };

  checkConnection = async () => {
    this.setState({ isCheckingConnection: true });
    
    const connectionResult = await checkFirestoreConnection();
    
    this.setState({ 
      connectionState: connectionResult.state,
      isCheckingConnection: false
    });
    
    // If we're back online and had an error, attempt to reset the error state
    if (connectionResult.success && this.state.hasError) {
      this.setState({ 
        hasError: false, 
        error: null 
      });
    }
    
    return connectionResult;
  };

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true, 
      error,
      isOffline: !navigator.onLine,
      connectionState: !navigator.onLine ? ConnectionState.OFFLINE : ConnectionState.OTHER_ERROR,
      isCheckingConnection: false,
      retryCount: 0
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to an error reporting service
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    
    // Check if it's a Firebase auth error
    if (
      error.message?.includes('auth') || 
      error.message?.includes('permission') ||
      error.message?.includes('unauthorized') ||
      error.message?.includes('unauthenticated')
    ) {
      this.setState({ connectionState: ConnectionState.AUTH_ERROR });
    }
  }

  handleRetry = async () => {
    this.setState({ 
      isCheckingConnection: true,
      retryCount: this.state.retryCount + 1
    });

    // For auth errors, try to refresh auth
    if (this.state.connectionState === ConnectionState.AUTH_ERROR) {
      const authRefreshed = await refreshFirebaseAuth();
      if (authRefreshed) {
        // Check connection again
        const connectionResult = await this.checkConnection();
        
        if (connectionResult.success) {
          // Reset error state if connection is successful
          this.setState({ 
            hasError: false, 
            error: null
          });
          return;
        }
      }
      
      // If retries are exhausted, suggest a clean reload
      if (this.state.retryCount >= 3) {
        return;
      }
    }
    
    // For other errors or if auth refresh didn't help
    this.setState({ 
      hasError: false, 
      error: null,
      isCheckingConnection: false
    });
    
    // Restart connection monitoring
    this.startConnectionMonitoring();
    
    // Reload the page for a fresh start
    window.location.reload();
  };
  
  handleCleanReload = () => {
    reloadAppWithCleanState();
  };

  render() {
    const { hasError, error, isOffline, connectionState, isCheckingConnection, retryCount } = this.state;

    // If checking connection, show loading state
    if (isCheckingConnection) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', p: 3 }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Checking connection...
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Please wait while we verify your connection to our services.
          </Typography>
        </Box>
      );
    }

    // If offline, show offline message
    if (isOffline || connectionState === ConnectionState.OFFLINE) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', p: 3 }}>
          <WifiOff sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            You are offline
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Please check your internet connection and try again. Some features may be limited while offline.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={this.handleRetry}
            disabled={isCheckingConnection}
          >
            Try Again
          </Button>
        </Box>
      );
    }

    // If there's an authentication error
    if (connectionState === ConnectionState.AUTH_ERROR) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', p: 3 }}>
          <Security sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            We're having trouble authenticating your session. This might be due to an expired session or permission changes.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={this.handleRetry}
              disabled={isCheckingConnection}
            >
              Refresh Session
            </Button>
            {retryCount >= 2 && (
              <Button 
                variant="outlined" 
                color="secondary" 
                onClick={this.handleCleanReload}
              >
                Clear Cache & Reload
              </Button>
            )}
          </Box>
        </Box>
      );
    }

    // If there's an error (but we're online), show error message
    if (hasError) {
      // Check if the error is firebase related
      const isFirebaseError = error?.toString().includes('firebase') || 
                             error?.toString().includes('Firestore');
      
      return (
        <Box sx={{ p: 3 }}>
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            icon={<ErrorOutline fontSize="inherit" />}
          >
            <AlertTitle>
              {isFirebaseError ? 'Connection Error' : 'Something went wrong'}
            </AlertTitle>
            {isFirebaseError 
              ? 'There was a problem connecting to our services. This might be due to network issues or a temporary service disruption.'
              : error?.message || 'An unexpected error occurred. Please try again later.'}
          </Alert>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={this.handleRetry}
              disabled={isCheckingConnection}
            >
              Try Again
            </Button>
            {retryCount >= 2 && (
              <Button 
                variant="outlined" 
                color="secondary" 
                onClick={this.handleCleanReload}
              >
                Reset & Reload
              </Button>
            )}
          </Box>
        </Box>
      );
    }

    // If no error or not offline, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary; 